import { useState, useEffect } from "react"
import { useAppContext } from "@ciq/shared/hooks/context"
import Api from "@ciq/shared/api/api"
import { formatTimestamp } from "@ciq/shared/utils/format"
import "./BuildInfoBadge.css"

/**
 * Displays build metadata (environment, commit hash, last PR, deploy timestamp)
 * in a fixed-position badge with clickable links. Only visible to SysAdmin users.
 * Shows a distinct local dev badge with branch name when running locally.
 * Fetches data from GET /health on mount.
 *
 * @returns {JSX.Element|null} The badge element, or null for non-admin users
 */
export const BuildInfoBadge = () => {
  const { isSystemAdmin } = useAppContext()
  const [buildInfo, setBuildInfo] = useState(null)

  useEffect(() => {
    if (!isSystemAdmin) return

    const controller = new AbortController()
    Api.get("/health", { signal: controller.signal })
      .then((response) => setBuildInfo(response.data))
      .catch(() => {}) // Diagnostic badge: silent fail is intentional for all error types
                    // (AbortError from cleanup, network failures, non-OK responses).
                    // Badge absence is the expected failure state — no logging/telemetry needed.

    return () => controller.abort()
  }, [isSystemAdmin])

  if (!isSystemAdmin || !buildInfo) return null

  const isLocal = buildInfo.environment === "local"

  if (isLocal) {
    const branch = buildInfo.git_branch ?? "unknown"
    const shortCommit = buildInfo.git_commit?.slice(0, 7) ?? "—"
    return (
      <div className="build-info-badge build-info-badge--local" data-testid="build-info-badge">
        <span className="build-info-badge__label">LOCAL</span>
        <span className="build-info-badge__separator">·</span>
        <span className="build-info-badge__item">{branch}</span>
        <span className="build-info-badge__separator">·</span>
        <span className="build-info-badge__item">{shortCommit}</span>
      </div>
    )
  }

  const envLabel = (buildInfo.environment ?? "—").toUpperCase()
  const shortCommit = buildInfo.git_commit?.slice(0, 7) ?? "—"
  const repoUrl = buildInfo.github_repo_url
  const deployedAt = buildInfo.deployed_at ? formatTimestamp(buildInfo.deployed_at) : "—"

  return (
    <div className="build-info-badge" data-testid="build-info-badge">
      <span className="build-info-badge__label">{envLabel}</span>
      <span className="build-info-badge__separator">·</span>
      {repoUrl && buildInfo.git_commit ? (
        <a
          className="build-info-badge__link"
          href={`${repoUrl}/commit/${buildInfo.git_commit}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {shortCommit}
        </a>
      ) : (
        <span className="build-info-badge__item">{shortCommit}</span>
      )}
      <span className="build-info-badge__separator">·</span>
      {repoUrl && buildInfo.last_pr_number ? (
        <>
          <a
            className="build-info-badge__link"
            href={`${repoUrl}/pull/${buildInfo.last_pr_number}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            #{buildInfo.last_pr_number}
          </a>
          {buildInfo.last_pr_title && (
            <span
              className="build-info-badge__pr-title"
              title={buildInfo.last_pr_title}
            >
              {buildInfo.last_pr_title}
            </span>
          )}
          <span className="build-info-badge__separator">·</span>
        </>
      ) : null}
      <span className="build-info-badge__item">{deployedAt}</span>
    </div>
  )
}
